import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { devFilesApi, prodDeployApi, preferencesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import { suggestNextVersion } from '../../utils/version';
import { hasColumnFilters, NO_MATCH_LIST, SEARCH_HINT } from '../../utils/listSearch';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import { useListTrunk } from '../../hooks/useListTrunk';
import { useLocation } from 'react-router-dom';
import { entryMemoryKey, loadView, saveView } from '../../utils/viewMemory';
import NoMatchState from '../../components/common/NoMatchState';
import AdvancedSearchModal from '../../components/common/AdvancedSearchModal';
import {
  RefreshCw, Plus, Trash2, X, Save, Pencil, History,
  ChevronLeft, ChevronRight, GripVertical,
  List, Table, FilterX, Upload, Activity, ListFilter, Lock, LockOpen
} from 'lucide-react';
import StuiSelect from '../../components/common/StuiSelect';
import { useLookups } from '../../context/LookupContext';
import { formatDateTime, formatDateOnly } from '../../utils/dateTime';

// Type is the magnitude of a change, Category what kind of change (Keep a Changelog). Both come
// from Lookup Values, the same rows the API validates against. Release is stamped only by Pull
// From PreProd, so it is shown but never offered.
const LK_TYPE = 'RevLog_Type';
const LK_CATEGORY = 'RevLog_Category';
const RELEASE = 'Release';

const TYPE_CLASS = { major: 'stui-badge-primary', minor: 'stui-badge-secondary', patch: 'stui-badge-indigo' };
const CATEGORY_CLASS = {
  Added: 'stui-badge-green', Changed: 'stui-badge-blue', Deprecated: 'stui-badge-amber', Removed: 'stui-badge-orange',
  Fixed: 'stui-badge-teal', Security: 'stui-badge-red', Release: 'stui-badge-indigo',
};
// Historical entries carry fix / feature / bugfix; anything outside the lookup renders gray with its raw value.
const getTypeClass = (type) => TYPE_CLASS[type] || 'stui-badge-gray';
const getCategoryClass = (category) => CATEGORY_CLASS[category] || 'stui-badge-gray';
const typeLabel = (type) => String(type || '').toUpperCase();
const typeShort = (type) => String(type || '').slice(0, 3).toUpperCase();
const capitalize = (v) => v ? v.charAt(0).toUpperCase() + v.slice(1) : v;

// The API answers a validation failure with a plain-string body naming the valid values.
// Prod pull lock: a SysAdmin-only system default in prod; ProdDeployController refuses Execute while set.
const PULL_LOCK_KEY = 'system.prodPullLock';

const errText = (err, fallback) =>
  typeof err.response?.data === 'string' ? err.response.data : err.response?.data?.message || err.response?.data?.error || fallback;

// A select of lookup values that still shows a stored value the lookup no longer offers.
const optionsWith = (options, current) =>
  current && !options.includes(current) ? [...options, current] : options;

// `changes` carries the bulk of this page's text — 411 revisions hold ~272K characters of
// changelog prose against ~33K in the summaries — so leaving it out made the search box blind
// to 91% of what it was sitting on.
// What search reads, labelled as the user sees it. The labels double as field: prefixes and feed
// the Advanced Search builder's field list.
const SEARCH_FIELD_LABELS = [
  { key: 'version', label: 'Version' }, { key: 'summary', label: 'Summary' }, { key: 'type', label: 'Type' },
  { key: 'category', label: 'Category' }, { key: 'date', label: 'Date' }, { key: 'changes', label: 'Changes' },
];
const SEARCH_FIELDS = SEARCH_FIELD_LABELS.map(f => f.key);

// ── Workflow status badge helper ──────────────────────────────────────────
const WfStatusRow = ({ label, status, conclusion }) => {
  const getBadge = () => {
    if (!status || status === 'none' || status === 'unknown') return { text: '— Unknown', cls: 'stui-badge-gray' };
    if (status === 'queued')      return { text: '⏳ Queued',   cls: 'stui-badge-amber' };
    if (status === 'in_progress') return { text: '⟳ Running',  cls: 'stui-badge-blue' };
    if (status === 'completed') {
      if (conclusion === 'success') return { text: '✓ Success', cls: 'stui-badge-green' };
      if (conclusion === 'failure') return { text: '✗ Failed',  cls: 'stui-badge-red' };
      return { text: `✓ ${conclusion || 'Done'}`, cls: 'stui-badge-green' };
    }
    return { text: status, cls: 'stui-badge-gray' };
  };
  const badge = getBadge();
  return (
    <div className="stui-row-between">
      <span className="stui-fw-medium" style={{ fontSize: 'var(--text-sm)' }}>{label}</span>
      <span className={badge.cls}>{badge.text}</span>
    </div>
  );
};

const RevLogPage = () => {
  // ── Shared ────────────────────────────────────────────────────────────────
  const [revLog, setRevLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // How this page was left in THIS history entry (utils/viewMemory): Back restores it, a nav
  // click or new link is a new entry and starts clean. Read once per mount.
  const location = useLocation();
  const memoryKey = entryMemoryKey(location, 'rev-log');
  const [saved] = useState(() => loadView(memoryKey));
  const [viewMode, setViewMode] = useState(saved?.view === 'datasheet' ? 'datasheet' : 'list');
  const [searchTerm, setSearchTerm] = useState(saved?.search || '');
  const { notify } = useNotifications();
  const { isSysAdmin, realUser } = useAuth();
  const isProd = !!window.__config__?.IS_PROD;
  const { getValues } = useLookups();
  const typeOptions = getValues(LK_TYPE);
  const categoryOptions = getValues(LK_CATEGORY).filter(c => c !== RELEASE);

  // ── List view ─────────────────────────────────────────────────────────────
  const [pickedRevision, setSelectedRevision] = useState(saved?.picked != null ? { version: saved.picked } : null);
  const [dsSort, setDsSort] = useState(saved?.sort?.key ? saved.sort : null); // header sort, shared by both views
  const [showAdvSearch, setShowAdvSearch] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [listCollapsed, setListCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // ── Add revision modal ────────────────────────────────────────────────────
  const [showAddRevision, setShowAddRevision] = useState(false);
  const [newRevision, setNewRevision] = useState({ version: '', type: 'minor', category: '', summary: '', changes: [''] });
  // Tracks whether the version field has been manually edited — once true, we stop auto-suggesting.
  const [versionTouched, setVersionTouched] = useState(false);

  // ── Datasheet state ───────────────────────────────────────────────────────
  const [dsFilters, setDsFilters] = useState(saved?.filters || {});
  const [dsEditMode, setDsEditMode] = useState(false);
  const [dsEdits, setDsEdits] = useState({});
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [changesModalRev, setChangesModalRev] = useState(null);
  const [changesLocal, setChangesLocal] = useState([]);

  // ── Prod deploy modal ─────────────────────────────────────────────────────
  const [showProdDeploy, setShowProdDeploy] = useState(false);
  const [undeployed, setUndeployed] = useState([]);
  const [undeployedLoading, setUndeployedLoading] = useState(false);
  const [deployForm, setDeployForm] = useState({ version: '', type: 'major', summary: '' });
  const [deployState, setDeployState] = useState('idle'); // idle | executing | success | error
  const [deployError, setDeployError] = useState('');
  const [deployResult, setDeployResult] = useState(null);
  // Pull lock = the system-tier default of PULL_LOCK_KEY; Execute refuses server-side while it exists.
  // undefined = not read yet, null = unlocked, { error } = could not be read
  const [pullLock, setPullLock] = useState(undefined);
  const [lockEditing, setLockEditing] = useState(false);
  const [lockComment, setLockComment] = useState('');
  const [lockBusy, setLockBusy] = useState(false);

  // ── WF status modal ───────────────────────────────────────────────────────
  const [showWfStatus, setShowWfStatus] = useState(false);
  const [wfStatus, setWfStatus] = useState(null);
  const [wfLoading, setWfLoading] = useState(false);
  const [wfLastChecked, setWfLastChecked] = useState(null);
  const [wfPolling, setWfPolling] = useState(false);
  const pollRef = useRef(null);

  // ── Data loading ──────────────────────────────────────────────────────────
  const loadRevLog = useCallback(async () => {
    try {
      setLoading(true);
      const response = await devFilesApi.getRevLog();
      setRevLog(response.data);
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load revision log');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadRevLog(); }, [loadRevLog]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup poll on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // ── Filtered data ─────────────────────────────────────────────────────────
  // The one trunk every view reads (sort -> search -> filters): list view maps it, the datasheet
  // receives it as `data`, the count chip measures it. See hooks/useListTrunk.
  const { searched: searchedRevisions, filtered: filteredRevisions, selected: selectedRevision } = useListTrunk({
    rows: revLog?.revisions, query: searchTerm, searchFields: SEARCH_FIELDS, labels: SEARCH_FIELD_LABELS,
    filters: dsFilters, sortConfig: dsSort, picked: pickedRevision, selectionKey: 'version', keep: isEditing,
  });

  const dsDisplayData = filteredRevisions;

  // A new history entry under a mounted page — clicking this page's own nav item — starts clean
  // (or restores, if the entry was visited before). A remount already reads it via useState.
  const entryKeyRef = useRef(memoryKey);
  useEffect(() => {
    if (entryKeyRef.current === memoryKey) return;
    entryKeyRef.current = memoryKey;
    const s = loadView(memoryKey);
    setViewMode(s?.view === 'datasheet' ? 'datasheet' : 'list');
    setSearchTerm(s?.search || '');
    setDsFilters(s?.filters || {});
    setDsSort(s?.sort?.key ? s.sort : null);
    setSelectedRevision(s?.picked != null ? { version: s.picked } : null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoryKey]);

  // Remember this page's state against the current history entry.
  useEffect(() => {
    saveView(memoryKey, { view: viewMode, search: searchTerm, filters: dsFilters, sort: dsSort, picked: (selectedRevision ?? pickedRevision)?.version ?? null });
  }, [memoryKey, viewMode, searchTerm, dsFilters, dsSort, selectedRevision, pickedRevision]);

  const hasChanges = isEditing && editForm && selectedRevision && (
    editForm.version !== selectedRevision.version ||
    editForm.type !== selectedRevision.type ||
    editForm.category !== selectedRevision.category ||
    editForm.summary !== selectedRevision.summary ||
    editForm.deployedToProd !== selectedRevision.deployedToProd ||
    JSON.stringify(editForm.changes) !== JSON.stringify(selectedRevision.changes)
  );

  const dsIsDirty = Object.keys(dsEdits).length > 0;
  const dsHasFilters = hasColumnFilters(dsFilters);
  const dsFilteredCount = filteredRevisions.length;

  const dsColumns = useMemo(() => [
    // Versions are [prefix][major].[minor], so sorting them as raw text puts a9.000 after
    // a10.000. Pad both numeric halves to a fixed width and the string compare becomes correct.
    { key: 'version',  fieldName: 'version',  label: 'Version', width: 100,
      sortValue: (v) => String(v || '').replace(/(\d+)/g, (n) => n.padStart(6, '0')) },
    { key: 'type',     fieldName: 'type',     label: 'Type',    width: 100,
      formatter: (v) => v ? <span className={getTypeClass(v)}>{typeLabel(v)}</span> : null },
    { key: 'category', fieldName: 'category', label: 'Category', width: 110,
      formatter: (v) => v ? <span className={getCategoryClass(v)}>{v}</span> : null },
    { key: 'date',     fieldName: 'date',     label: 'Date',    width: 120, formatter: (v) => formatDateOnly(v) },
    { key: 'summary',  fieldName: 'summary',  label: 'Summary', width: 600 },
  ], []); // eslint-disable-line react-hooks/exhaustive-deps

  const renderDsCell = (row, col, displayValue) => {
    switch (col.key) {
      case 'type':
        return (
          <StuiSelect className="ds-cell-input" value={displayValue ?? 'minor'} onChange={e => handleDsEdit(row.id, 'type', e.target.value)}>
            {optionsWith(typeOptions, row.type).map(t => <option key={t} value={t} disabled={!typeOptions.includes(t)}>{capitalize(t)}</option>)}
          </StuiSelect>
        );
      case 'category':
        // Release belongs to Pull From PreProd: shown, never editable here.
        if (row.category === RELEASE) return <span className={getCategoryClass(RELEASE)}>{RELEASE}</span>;
        return (
          <StuiSelect className="ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'category', e.target.value)}>
            {!row.category && <option value="">—</option>}
            {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </StuiSelect>
        );
      case 'summary':
        return <input className="ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'summary', e.target.value)} />;
      default:
        return displayValue;
    }
  };

  // ── List view handlers ────────────────────────────────────────────────────
  const handleSelectRevision = (revision) => {
    if (isEditing) {
      notify.confirm('Discard unsaved changes?', () => { cancelEditing(); setSelectedRevision(revision); });
      return;
    }
    setSelectedRevision(revision);
  };

  const startEditing = () => {
    if (!selectedRevision) return;
    setEditForm({ ...selectedRevision, changes: [...selectedRevision.changes] });
    setIsEditing(true);
  };

  const cancelEditing = () => { setIsEditing(false); setEditForm({}); };

  const handleSave = async () => {
    try {
      setSaving(true);
      await devFilesApi.updateRevision(selectedRevision.id, editForm);
      setIsEditing(false);
      setEditForm({});
      await loadRevLog();
    } catch (err) {
      notify.error(errText(err, 'Failed to save revision'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddRevision = async () => {
    if (!newRevision.version.trim()) return;
    if (!newRevision.summary.trim()) return;
    const filteredChanges = newRevision.changes.filter(c => c.trim());
    if (filteredChanges.length === 0) return;
    if (!newRevision.category) return;
    try {
      setSaving(true);
      await devFilesApi.addRevision({
        version: newRevision.version.trim(),
        type: newRevision.type,
        category: newRevision.category,
        summary: newRevision.summary,
        changes: filteredChanges
      });
      setNewRevision({ version: '', type: 'minor', category: '', summary: '', changes: [''] });
      setVersionTouched(false);
      setShowAddRevision(false);
      await loadRevLog();
    } catch (err) {
      notify.error(errText(err, 'Failed to add revision'));
    } finally {
      setSaving(false);
    }
  };

  // Auto-populate the version field when the Add modal opens or type changes,
  // unless the user has manually edited the version input.
  useEffect(() => {
    if (!showAddRevision) return;
    if (versionTouched) return;
    const suggested = suggestNextVersion(revLog?.currentVersion, newRevision.type);
    setNewRevision(prev => ({ ...prev, version: suggested }));
  }, [showAddRevision, newRevision.type, revLog?.currentVersion, versionTouched]);

  const handleDeleteRevision = () => {
    if (!selectedRevision) return;
    if (revLog.revisions.length === 1) { notify.error('Cannot delete the only revision'); return; }
    notify.confirm(`Delete revision ${selectedRevision.version}?`, async () => {
      try {
        setSaving(true);
        await devFilesApi.deleteRevision(selectedRevision.id);
        setIsEditing(false);
        setSelectedRevision(null);
        await loadRevLog();
      } catch (err) {
        notify.error(err.response?.data?.message || 'Failed to delete revision');
      } finally {
        setSaving(false);
      }
    });
  };

  const addChangeLine = (isEdit = false) => {
    if (isEdit) setEditForm({ ...editForm, changes: [...editForm.changes, ''] });
    else setNewRevision({ ...newRevision, changes: [...newRevision.changes, ''] });
  };

  const updateChangeLine = (index, value, isEdit = false) => {
    if (isEdit) {
      const c = [...editForm.changes]; c[index] = value; setEditForm({ ...editForm, changes: c });
    } else {
      const c = [...newRevision.changes]; c[index] = value; setNewRevision({ ...newRevision, changes: c });
    }
  };

  const removeChangeLine = (index, isEdit = false) => {
    if (isEdit) {
      const c = editForm.changes.filter((_, i) => i !== index);
      setEditForm({ ...editForm, changes: c.length ? c : [''] });
    } else {
      const c = newRevision.changes.filter((_, i) => i !== index);
      setNewRevision({ ...newRevision, changes: c.length ? c : [''] });
    }
  };

  // ── Datasheet handlers ────────────────────────────────────────────────────
  const handleDsEdit = (id, field, value) => {
    setDsEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const handleDsCancel = () => { setDsEdits({}); setDsEditMode(false); };

  const handleDsSave = async () => {
    const dirtyIds = Object.keys(dsEdits);
    if (dirtyIds.length === 0) return;
    try {
      setSaving(true);
      for (const id of dirtyIds) {
        const original = revLog.revisions.find(r => r.id === parseInt(id));
        if (!original) continue;
        await devFilesApi.updateRevision(original.id, { ...original, ...dsEdits[id] });
      }
      setDsEdits({});
      setDsEditMode(false);
      notify.success('Changes saved');
      await loadRevLog();
    } catch (err) {
      notify.error(errText(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const openChangesModal = (rev) => {
    const merged = { ...rev, ...(dsEdits[rev.id] || {}) };
    setChangesModalRev(rev);
    setChangesLocal([...(merged.changes || [])]);
    setShowChangesModal(true);
  };

  const handleChangesApply = () => {
    if (!changesModalRev) return;
    const filtered = changesLocal.filter(c => c.trim());
    setDsEdits(prev => ({ ...prev, [changesModalRev.id]: { ...(prev[changesModalRev.id] || {}), changes: filtered.length ? filtered : [''] } }));
    setShowChangesModal(false);
  };

  // ── Pull lock ─────────────────────────────────────────────────────────────
  const loadPullLock = async () => {
    setPullLock(undefined);
    try {
      const res = await preferencesApi.getDefaults();
      const row = (res.data?.defaults || []).find(d => d.prefKey === PULL_LOCK_KEY && d.databaseInstanceId == null && d.role == null);
      let value = null;
      if (row) { try { value = JSON.parse(row.value); } catch { value = { comment: row.value }; } }
      setPullLock(value);
    } catch (err) {
      setPullLock({ error: errText(err, 'Could not read the pull lock') });
    }
  };

  const writePullLock = async (value) => {
    setLockBusy(true);
    try {
      await preferencesApi.saveDefaults([{ prefKey: PULL_LOCK_KEY, databaseInstanceId: null, role: null, value }]);
      setLockEditing(false);
      setLockComment('');
      await loadPullLock();
    } catch (err) {
      notify.error(errText(err, 'Failed to update the pull lock'));
    } finally {
      setLockBusy(false);
    }
  };

  const handleLock = () => writePullLock(JSON.stringify({
    lockedBy: realUser?.user_Name || realUser?.email || 'unknown',
    lockedOn: new Date().toISOString(),
    comment: lockComment.trim(),
  }));

  const handleUnlock = () => writePullLock(null);

  // ── Prod deploy handlers ──────────────────────────────────────────────────
  const openProdDeploy = async () => {
    setDeployForm({ version: '', type: 'major', summary: '' });
    setDeployState('idle');
    setDeployError('');
    setDeployResult(null);
    setShowProdDeploy(true);
    setLockEditing(false);
    setLockComment('');
    loadPullLock();
    setUndeployedLoading(true);
    try {
      const res = await prodDeployApi.getUndeployed();
      setUndeployed(res.data);
    } catch {
      notify.error('Failed to load preprod revisions');
    } finally {
      setUndeployedLoading(false);
    }
  };

  const handleDeploy = async () => {
    setDeployState('executing');
    setDeployError('');
    try {
      const res = await prodDeployApi.execute({
        version: deployForm.version.trim(),
        type: deployForm.type,
        summary: deployForm.summary.trim(),
        preprodIds: undeployed.map(e => e.id)
      });
      setDeployResult(res.data);
      setDeployState('success');
      await loadRevLog();
    } catch (err) {
      setDeployError(errText(err, 'Deploy failed'));
      setDeployState('error');
    }
  };

  // ── WF status handlers ────────────────────────────────────────────────────
  const openWfStatus = async () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setWfStatus(null);
    setWfLoading(true);
    setShowWfStatus(true);
    try {
      const res = await prodDeployApi.workflowStatus();
      setWfStatus(res.data);
      setWfLastChecked(new Date());
      const { backendStatus, frontendStatus } = res.data;
      const isActive = (s) => s === 'in_progress' || s === 'queued';
      if (isActive(backendStatus) || isActive(frontendStatus)) {
        setWfPolling(true);
        pollRef.current = setInterval(async () => {
          try {
            const r = await prodDeployApi.workflowStatus();
            setWfStatus(r.data);
            setWfLastChecked(new Date());
            if (r.data.backendStatus === 'completed' && r.data.frontendStatus === 'completed') {
              clearInterval(pollRef.current);
              setWfPolling(false);
            }
          } catch { clearInterval(pollRef.current); setWfPolling(false); }
        }, 10000);
      }
    } catch {
      notify.error('Failed to get workflow status');
    } finally {
      setWfLoading(false);
    }
  };

  const closeWfStatus = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setWfPolling(false);
    setShowWfStatus(false);
  };

  if (loading) return <div className="dev-page loading">Loading...</div>;

  const pullLocked = pullLock !== null;
  const deployCanExecute = deployState !== 'executing' && !pullLocked && deployForm.version.trim() && deployForm.summary.trim() && undeployed.length > 0;

  return (
    <div className="module-container">

      {/* ── Header ── */}
      <div className="module-header">
        <div className="module-header-left">
          <History size={20} style={{ color: 'var(--color-primary-text)' }} />
          <h1>Rev Log</h1>
          <div className="module-header-tabs">
            <button onClick={() => setViewMode('list')} className={`tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`} title="List View"><List size={18} /></button>
            <button onClick={() => setViewMode('datasheet')} className={`tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`} title="Datasheet View"><Table size={18} /></button>
          </div>
          <span className="module-header-meta">
            v{revLog?.currentVersion} &bull; {revLog?.revisions.length} revision{revLog?.revisions.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="module-header-actions">

          {/* List view CRUD */}
          {viewMode === 'list' && (
            <>
              {isEditing
                ? <button className="crud-btn stui-text-error" onClick={handleDeleteRevision} disabled={saving || revLog?.revisions.length === 1} title="Delete"><Trash2 size={18} /></button>
                : <button className="crud-btn" onClick={() => setShowAddRevision(true)} title="Add Revision"><Plus size={18} /></button>
              }
              <button className={`crud-btn ${!selectedRevision && !isEditing ? 'disabled' : ''}`} onClick={isEditing ? cancelEditing : startEditing} disabled={!selectedRevision && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
                {isEditing ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`crud-btn ${!isEditing || !hasChanges ? 'disabled' : ''}`} onClick={handleSave} disabled={!isEditing || saving || !hasChanges} title="Save"><Save size={18} /></button>
            </>
          )}

          {/* Datasheet controls */}
          {viewMode === 'datasheet' && (
            <>
              <button className="crud-btn" onClick={dsEditMode ? handleDsCancel : () => setDsEditMode(true)} title={dsEditMode ? 'Cancel' : 'Edit'}>
                {dsEditMode ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`crud-btn ${!dsIsDirty || saving ? 'disabled' : ''}`} onClick={handleDsSave} disabled={!dsIsDirty || saving} title="Save"><Save size={18} /></button>
            </>
          )}

          {/* Clear filter */}
          {dsHasFilters && (
            <button onClick={() => setDsFilters({})} className="clear-filter-btn" title="Clear filters">
              <FilterX size={16} /><span>{dsFilteredCount}/{revLog?.revisions.length || 0}</span>
            </button>
          )}

          {/* Search */}
          <div className="stui-search-wrapper">
            <SearchClearIcon size={14} searchTerm={searchTerm} onClear={() => setSearchTerm('')} />
            <input
              className="stui-search-input"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search revisions..."
              title={SEARCH_HINT}
            />
          </div>

          {/* 6-dot */}
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); setShowAdvSearch(true); }}><ListFilter size={16} /><span>Advanced Search…</span></button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); loadRevLog(); }}><RefreshCw size={16} /><span>Refresh</span></button>
                {isProd && isSysAdmin && (
                  <>
                    <div className="stui-action-menu-divider" />
                    <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); openProdDeploy(); }}><Upload size={16} /><span>Pull from Pre-Prod…</span></button>
                    <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); openWfStatus(); }}><Activity size={16} /><span>GitHub Actions Status…</span></button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="list-view">

        {/* ── List view ── */}
        {viewMode === 'list' && (
          <>
            {listCollapsed ? (
              <div className="list-panel-collapsed">
                <button onClick={() => setListCollapsed(false)} className="list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button>
              </div>
            ) : (
              <div className="list-panel">
                <div className="list-panel-header">
                  <button onClick={() => setListCollapsed(true)} className="list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
                </div>
                <div className="list-scroll">
                  {filteredRevisions.length > 0 ? filteredRevisions.map(revision => (
                    <button key={revision.version} onClick={() => handleSelectRevision(revision)} className={`list-item-btn ${selectedRevision?.version === revision.version ? 'selected' : ''}`}>
                      <span className="list-item-primary">
                        <span className={`${getTypeClass(revision.type)} stui-badge-xs stui-mr-sm`}>
                          {typeShort(revision.type)}
                        </span>
                        {revision.version}
                        {revision.deployedToProd && (
                          <span className="stui-badge-green stui-badge-xs stui-ml-xs">PROD</span>
                        )}
                      </span>
                      <span className="list-item-secondary">{formatDateOnly(revision.date)}</span>
                    </button>
                  )) : <div className="stui-list-empty">{revLog?.revisions?.length ? NO_MATCH_LIST : 'No revisions'}</div>}
                </div>
              </div>
            )}

            {/* Detail panel */}
            <div className="detail-panel">
              {selectedRevision ? (
                <div className="detail-content" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

                  {/* General Information — fixed */}
                  <div className="stui-shrink-0">
                    <div className="stui-detail-grid">

                      <div className="stui-col-24">
                        <div className="stui-detail-section-header">General Information</div>
                      </div>

                      <div className="stui-field-group stui-col-5">
                        <label>Version</label>
                        {isEditing
                          ? <input type="text" className="stui-field-input" value={editForm.version ?? ''} onChange={e => setEditForm({ ...editForm, version: e.target.value })} />
                          : <input type="text" className="stui-field-input" value={selectedRevision.version} readOnly onChange={() => {}} />
                        }
                      </div>

                      <div className="stui-field-group stui-col-4">
                        <label>Type</label>
                        {isEditing
                          ? <StuiSelect className="stui-field-input" value={editForm.type} onChange={e => setEditForm({ ...editForm, type: e.target.value })}>
                              {optionsWith(typeOptions, selectedRevision.type).map(t => <option key={t} value={t} disabled={!typeOptions.includes(t)}>{capitalize(t)}</option>)}
                            </StuiSelect>
                          : <span className={getTypeClass(selectedRevision.type)}>{typeLabel(selectedRevision.type)}</span>
                        }
                      </div>

                      <div className="stui-field-group stui-col-5">
                        <label>Category</label>
                        {isEditing && selectedRevision.category !== RELEASE
                          ? <StuiSelect className="stui-field-input" value={editForm.category ?? ''} onChange={e => setEditForm({ ...editForm, category: e.target.value })}>
                              {!selectedRevision.category && <option value="">—</option>}
                              {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                            </StuiSelect>
                          : selectedRevision.category
                            ? <span className={getCategoryClass(selectedRevision.category)}>{selectedRevision.category}</span>
                            : <span className="stui-text-muted">—</span>
                        }
                      </div>

                      <div className="stui-field-group stui-col-5">
                        <label>Date</label>
                        <input type="text" className="stui-field-input" value={formatDateOnly(selectedRevision.date)} readOnly onChange={() => {}} />
                      </div>

                      <div className="stui-field-group stui-col-5">
                        <label>Deployed to Prod</label>
                        {isEditing
                          ? <label className="stui-row-8" style={{ height: 'var(--input-height)', fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
                              <input type="checkbox" className="stui-checkbox-input" checked={!!editForm.deployedToProd} onChange={e => setEditForm({ ...editForm, deployedToProd: e.target.checked })} />
                              <span>{editForm.deployedToProd ? 'Yes' : 'No'}</span>
                            </label>
                          : <span className="stui-row-8 stui-fw-medium" style={{ height: 'var(--input-height)', fontSize: 'var(--text-sm)', color: selectedRevision.deployedToProd ? 'var(--color-success)' : 'var(--color-gray-400)' }}>
                              {selectedRevision.deployedToProd ? '✓ Yes' : '—'}
                            </span>
                        }
                      </div>

                      <div className="stui-col-24">
                        <div className="stui-detail-section-header">Summary</div>
                      </div>

                      <div className="stui-field-group stui-col-24">
                        {isEditing
                          ? <input type="text" className="stui-field-input" value={editForm.summary} onChange={e => setEditForm({ ...editForm, summary: e.target.value })} autoFocus />
                          : <input type="text" className="stui-field-input" value={selectedRevision.summary} readOnly onChange={() => {}} />
                        }
                      </div>

                    </div>
                  </div>

                  {/* Changes — fills remaining space */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <div className="stui-detail-section-header stui-inner-table-section-header">
                      <span>Changes</span>
                      {isEditing && (
                        <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => addChangeLine(true)}>
                          <Plus size={12} /> Add
                        </button>
                      )}
                    </div>
                    <div className="stui-modal-scroll">
                      {isEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                          {editForm.changes.map((change, index) => (
                            <div key={index} className="stui-row-4">
                              <input type="text" className="stui-field-input stui-flex-1" value={change} onChange={e => updateChangeLine(index, e.target.value, true)} placeholder="Describe a change..." />
                              <button className="stui-btn stui-btn-danger-subtle stui-btn-icon" onClick={() => removeChangeLine(index, true)} disabled={editForm.changes.length === 1} title="Remove"><X size={16} /></button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <ul className="stui-m-0" style={{ paddingLeft: 'var(--spacing-lg)', lineHeight: '1.7' }}>
                          {selectedRevision.changes.map((change, index) => (
                            <li key={index} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)' }}>{change}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                </div>
              ) : (
                <div className="stui-detail-empty">
                  {(revLog?.revisions?.length || 0) > 0 && filteredRevisions.length === 0 ? (
                    <NoMatchState hasSearch={!!searchTerm} hasFilters={dsHasFilters} onClearSearch={() => setSearchTerm('')} onClearFilters={() => setDsFilters({})} />
                  ) : <p>Select a revision to view details</p>}
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Datasheet view ── */}
        {viewMode === 'datasheet' && (
          <div className="datasheet-view">
            <FilterableDataGrid
              data={dsDisplayData}
              allData={searchedRevisions}
              sortConfig={dsSort || { key: null, direction: null }}
              onSortChange={setDsSort}
              columns={dsColumns}
              filters={dsFilters}
              setFilters={setDsFilters}
              editModeEnabled={dsEditMode}
              editedData={dsEdits}
              renderEditableCell={renderDsCell}
              onRowDoubleClick={(row) => openChangesModal(row)}
            />
          </div>
        )}
      </div>

      {/* ── Add Revision Modal ── */}
      {showAddRevision && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3>New Revision</h3>
              <button className="stui-modal-close" onClick={() => setShowAddRevision(false)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-detail-grid" style={{ marginBottom: 0 }}>

                <div className="stui-col-24">
                  <div className="stui-detail-section-header">General Information</div>
                </div>

                <div className="stui-field-group stui-col-8">
                  <label>Version</label>
                  <input
                    type="text"
                    className="stui-field-input"
                    value={newRevision.version}
                    onChange={e => { setVersionTouched(true); setNewRevision({ ...newRevision, version: e.target.value }); }}
                    placeholder="e.g. a20.012"
                  />
                </div>

                <div className="stui-field-group stui-col-8">
                  <label>Type</label>
                  <StuiSelect className="stui-field-input" value={newRevision.type} onChange={e => setNewRevision({ ...newRevision, type: e.target.value })}>
                    {typeOptions.map(t => <option key={t} value={t}>{capitalize(t)}</option>)}
                  </StuiSelect>
                </div>

                <div className="stui-field-group stui-col-8">
                  <label>Category <span className="stui-required">*</span></label>
                  <StuiSelect className="stui-field-input" value={newRevision.category} onChange={e => setNewRevision({ ...newRevision, category: e.target.value })}>
                    <option value="">-- Select --</option>
                    {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </StuiSelect>
                </div>

                <div className="stui-field-group stui-col-24">
                  <label>Summary</label>
                  <input type="text" className="stui-field-input" value={newRevision.summary} onChange={e => setNewRevision({ ...newRevision, summary: e.target.value })} placeholder="Brief description of this release" autoFocus />
                </div>

                <div className="stui-col-24">
                  <div className="stui-detail-section-header stui-inner-table-section-header">
                    <span>Changes</span>
                    <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => addChangeLine()}>
                      <Plus size={12} /> Add
                    </button>
                  </div>
                </div>

                <div className="stui-col-24">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                    {newRevision.changes.map((change, index) => (
                      <div key={index} className="stui-row-4">
                        <input type="text" className="stui-field-input stui-flex-1" value={change} onChange={e => updateChangeLine(index, e.target.value)} placeholder="Describe a change..." />
                        <button className="stui-btn stui-btn-danger-subtle stui-btn-icon" onClick={() => removeChangeLine(index)} disabled={newRevision.changes.length === 1} title="Remove"><X size={16} /></button>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => { setShowAddRevision(false); setVersionTouched(false); }}>Cancel</button>
              <button className="stui-btn stui-btn-primary" onClick={handleAddRevision} disabled={saving || !newRevision.version.trim() || !newRevision.category || !newRevision.summary.trim() || !newRevision.changes.some(c => c.trim())}>
                {saving ? 'Adding...' : 'Add Revision'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Changes popup (datasheet double-click) ── */}
      {showChangesModal && changesModalRev && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-lg">
            <div className="stui-modal-header">
              <h3>Changes — {changesModalRev.version}</h3>
              <button className="stui-modal-close" onClick={() => setShowChangesModal(false)}><X size={20} /></button>
            </div>
            <div className="stui-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
              {dsEditMode ? (
                <>
                  {changesLocal.map((change, index) => (
                    <div key={index} className="stui-row-4">
                      <input type="text" className="stui-field-input stui-flex-1" value={change} onChange={e => { const c = [...changesLocal]; c[index] = e.target.value; setChangesLocal(c); }} placeholder="Describe a change..." autoFocus={index === 0} />
                      <button className="stui-btn stui-btn-danger-subtle stui-btn-icon" onClick={() => setChangesLocal(prev => { const c = prev.filter((_, i) => i !== index); return c.length ? c : ['']; })} disabled={changesLocal.length === 1} title="Remove"><X size={16} /></button>
                    </div>
                  ))}
                  <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setChangesLocal(prev => [...prev, ''])} style={{ alignSelf: 'flex-start' }}>
                    <Plus size={12} /> Add
                  </button>
                </>
              ) : (
                <ul className="stui-m-0" style={{ paddingLeft: 'var(--spacing-lg)', lineHeight: '1.9' }}>
                  {changesLocal.map((change, index) => (
                    <li key={index} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-700)' }}>{change}</li>
                  ))}
                </ul>
              )}
            </div>
            {dsEditMode && (
              <div className="stui-modal-footer">
                <button className="stui-btn stui-btn-secondary" onClick={() => setShowChangesModal(false)}>Cancel</button>
                <button className="stui-btn stui-btn-primary" onClick={handleChangesApply}>Apply</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Pull From PreProd Modal ── */}
      {showProdDeploy && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3>Pull From PreProd</h3>
              <div className="stui-row-4">
                {deployState !== 'success' && pullLock !== undefined && !pullLock?.error && (
                  pullLock
                    ? <button className="stui-modal-close" onClick={handleUnlock} disabled={lockBusy || deployState === 'executing'} title="Unlock pulls"><LockOpen size={16} /></button>
                    : <button className="stui-modal-close" onClick={() => setLockEditing(true)} disabled={lockBusy || lockEditing || deployState === 'executing'} title="Lock pulls"><Lock size={16} /></button>
                )}
                <button className="stui-modal-close" onClick={() => setShowProdDeploy(false)} disabled={deployState === 'executing'}><X size={20} /></button>
              </div>
            </div>
            <div className="stui-modal-body">
              {deployState !== 'success' && pullLock && !pullLock.error && (
                <div className="stui-inline-notice stui-inline-notice-warning stui-mb-sm">
                  <Lock size={14} />
                  <span>
                    <strong>Pulls locked by {pullLock.lockedBy || 'someone'}</strong>
                    {pullLock.lockedOn && <> · {formatDateTime(pullLock.lockedOn, { valueZone: 'utc' })}</>}
                    {pullLock.comment && <><br />{pullLock.comment}</>}
                  </span>
                </div>
              )}
              {deployState !== 'success' && pullLock?.error && (
                <div className="stui-inline-notice stui-inline-notice-error stui-mb-sm">{pullLock.error}</div>
              )}
              {deployState !== 'success' && lockEditing && (
                <div className="stui-detail-grid stui-mb-sm">
                  <div className="stui-field-group stui-col-24">
                    <label>Why are pulls locked? <span className="stui-required">*</span></label>
                    <input type="text" className="stui-field-input" value={lockComment} maxLength={500} onChange={e => setLockComment(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && lockComment.trim()) handleLock(); }} placeholder="e.g. Step 119 P1 in preprod, hold until validated" autoFocus disabled={lockBusy} />
                  </div>
                  <div className="stui-col-24 stui-row-8">
                    <button className="stui-btn stui-btn-secondary" onClick={() => { setLockEditing(false); setLockComment(''); }} disabled={lockBusy}>Cancel</button>
                    <button className="stui-btn stui-btn-primary" onClick={handleLock} disabled={lockBusy || !lockComment.trim()}><Lock size={14} /> Lock Pulls</button>
                  </div>
                </div>
              )}
              {deployState === 'success' ? (
                <div style={{ textAlign: 'center', padding: 'var(--spacing-lg) 0' }}>
                  <div className="stui-fw-semibold stui-mb-sm" style={{ color: 'var(--color-success-text)', fontSize: 'var(--text-lg)' }}>
                    ✓ Prod rev log updated — {deployResult?.version}
                  </div>
                  <div className="stui-text-muted" style={{ fontSize: 'var(--text-sm)' }}>
                    GitHub workflows triggered. Use GitHub Actions Status to monitor progress.
                  </div>
                </div>
              ) : (
                <div className="stui-detail-grid">

                  <div className="stui-field-group stui-col-10">
                    <label>Current Prod Version</label>
                    <input type="text" className="stui-field-input" value={revLog?.currentVersion || '—'} readOnly />
                  </div>

                  <div className="stui-col-14" />

                  <div className="stui-field-group stui-col-10">
                    <label>New Version <span className="stui-required">*</span></label>
                    <input type="text" className="stui-field-input" value={deployForm.version} onChange={e => setDeployForm({ ...deployForm, version: e.target.value })} disabled={deployState === 'executing'} placeholder="e.g. a18.000" autoFocus />
                  </div>

                  <div className="stui-field-group stui-col-7">
                    <label>Type <span className="stui-required">*</span></label>
                    <StuiSelect className="stui-field-input" value={deployForm.type} onChange={e => setDeployForm({ ...deployForm, type: e.target.value })} disabled={deployState === 'executing'}>
                      {typeOptions.map(t => <option key={t} value={t}>{capitalize(t)}</option>)}
                    </StuiSelect>
                  </div>

                  <div className="stui-col-7" />

                  <div className="stui-field-group stui-col-24">
                    <label>Summary <span className="stui-required">*</span></label>
                    <input type="text" className="stui-field-input" value={deployForm.summary} onChange={e => setDeployForm({ ...deployForm, summary: e.target.value })} disabled={deployState === 'executing'} placeholder="Brief description of this production release" />
                  </div>

                  <div className="stui-col-24">
                    <div className="stui-detail-section-header">
                      Unpublished PreProd Changes ({undeployed.length})
                    </div>
                  </div>

                  <div className="stui-col-24">
                    {undeployedLoading ? (
                      <div className="stui-text-empty">Loading...</div>
                    ) : undeployed.length === 0 ? (
                      <div className="stui-text-empty">Nothing pending — all preprod changes already deployed.</div>
                    ) : (
                      <div className="stui-data-grid-container">
                        <div className="stui-data-grid-scroll-area" style={{ maxHeight: '180px' }}>
                          <table className="stui-data-grid stui-data-grid-auto">
                            <thead>
                              <tr>
                                <th>Type</th>
                                <th>Category</th>
                                <th>Version</th>
                                <th>Date</th>
                                <th>Summary</th>
                              </tr>
                            </thead>
                            <tbody>
                              {undeployed.map(e => (
                                <tr key={e.id}>
                                  <td><span className={getTypeClass(e.type)}>{typeShort(e.type)}</span></td>
                                  <td>{e.category ? <span className={getCategoryClass(e.category)}>{e.category}</span> : null}</td>
                                  <td className="stui-text-nowrap">{e.version}</td>
                                  <td className="stui-td-dim stui-text-nowrap">{formatDateOnly(e.date)}</td>
                                  <td>{e.summary}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>

                  {deployState === 'error' && (
                    <div className="stui-col-24 stui-text-error">{deployError}</div>
                  )}

                </div>
              )}
            </div>
            <div className="stui-modal-footer">
              {deployState === 'success' ? (
                <button className="stui-btn stui-btn-primary" onClick={() => setShowProdDeploy(false)}>Close</button>
              ) : (
                <>
                  <button className="stui-btn stui-btn-secondary" onClick={() => setShowProdDeploy(false)} disabled={deployState === 'executing'}>Cancel</button>
                  <button
                    className={`stui-btn ${deployCanExecute ? 'stui-btn-danger' : 'stui-btn-primary'}`}
                    onClick={handleDeploy}
                    disabled={!deployCanExecute}
                  >
                    {deployState === 'executing' ? 'Deploying...' : 'Execute'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── GitHub Actions Status Modal ── */}
      {showWfStatus && (
        <div className="stui-modal-overlay">
          <div className="stui-modal">
            <div className="stui-modal-header">
              <h3>GitHub Actions Status</h3>
              <button className="stui-modal-close" onClick={closeWfStatus}><X size={20} /></button>
            </div>
            <div className="stui-modal-body">
              {wfLoading && !wfStatus ? (
                <div className="stui-text-empty">Checking...</div>
              ) : wfStatus ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                  <WfStatusRow label="Backend Deploy" status={wfStatus.backendStatus} conclusion={wfStatus.backendConclusion} />
                  <WfStatusRow label="Frontend Deploy" status={wfStatus.frontendStatus} conclusion={wfStatus.frontendConclusion} />
                  <div className="stui-row-8" style={{ marginTop: 'var(--spacing-xs)', paddingTop: 'var(--spacing-xs)', borderTop: '1px solid var(--color-gray-100)', fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                    {wfPolling ? (
                      <>
                        <span className="stui-status-dot stui-status-dot-live" />
                        <span style={{ color: 'var(--color-primary-text)' }}>Polling every 10s</span>
                        <span className="stui-ml-auto">Last checked: {formatDateTime(wfLastChecked, { mode: 'time', valueZone: 'utc' })}</span>
                      </>
                    ) : (
                      <>
                        <span className="stui-status-dot" />
                        <span>Not polling</span>
                        <span className="stui-ml-auto">Last checked: {formatDateTime(wfLastChecked, { mode: 'time', valueZone: 'utc' })}</span>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="stui-text-empty">No status available.</div>
              )}
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={closeWfStatus}>Close</button>
            </div>
          </div>
        </div>
      )}

      <AdvancedSearchModal isOpen={showAdvSearch} onClose={() => setShowAdvSearch(false)} query={searchTerm} fields={SEARCH_FIELD_LABELS} onApply={setSearchTerm} />
    </div>
  );
};

export default RevLogPage;
